/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import Colors from 'constants/Colors';
import { Breakpoints } from "../../constants/Breakpoints";

const propTypes = {
  meta: PropTypes.shape({
    touched: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.object,
      PropTypes.array
    ]),
    error: PropTypes.string,
    isFocused: PropTypes.bool
  }),
  isRequired: PropTypes.bool,
  inline: PropTypes.bool,
  textColor: PropTypes.bool,
};

const defaultProps = {
  meta: undefined,
  isRequired: false,
  inline: false
};

const LabelText = styled.label`
  font-size: ${({ fontSize }) => fontSize || 13}px;
  ${({ color, disabled }) => disabled ? `color: ${Colors.GRAY[3]}` : color ? `color: ${color}` : `color: ${Colors.GRAY[6]}`};
`;

const Container = styled.div`
  display: inline-flex;
  ${({ width }) => width ? `width: ${width}%` : 'width: 100%;'};

  vertical-align: middle; 
  margin: 0;
  
  ${({ color }) => (color ? `color: ${color}` : `color: ${Colors.GRAY[6]}`)};

  overflow: hidden;
  //white-space: nowrap;
  text-overflow: ellipsis;
  ${({ wordBreak }) => (wordBreak ? "word-break: break-all;" : "")};
  ${({ wordWrap }) => (wordWrap ? "overflow-wrap: break-word;" : "")}
  


  ${({ textColor }) =>
    textColor
      ? css`
          ${textColor == Colors.PURPLE ? "font-weigth:bold !important;" : ""}
          color:${textColor};
        `
      : `color: ${Colors.GRAY[6]};`}

  ${({ setMinHeight }) =>
    setMinHeight &&
    css`
      min-height: 25px;
    `}

  & ${LabelText} {
    ${({ inverse }) =>
      inverse &&
      `
      color: ${Colors.WHITE};
    `}

    ${({ isRequired }) =>
      isRequired
        ? css`
            color: ${Colors.SECONDARY_MEDIUM};
          `
        : ""}
  
    ${({ meta }) =>
      meta && meta.touched && meta.error
        ? css`
            color: ${Colors.DANGER};
          `
        : ""}

    ${({ meta }) =>
      meta && meta.isFocused && !meta.error
        ? css`
            color: ${Colors.PRIMARY1};

            transition: color 0.15s ease;
          `
        : ""}

    font-weight: 700;
    transition: color 0.15s ease;
  }

  ${({ inline }) =>
    inline &&
    css`
      min-width: 40px;
      margin: 0 10px 0 0;
      overflow: unset;
      white-space: normal;
      line-height: 16px;
      display: flex;
      align-items: left;
      text-align: left;
      position: relative;

      @media screen and (min-width: 1750px) {
        width: 24%;
      }

      @media screen and (min-width: 2000px) {
        width: 22%;
      }

      @media screen and (min-width: 2200px) {
        width: 18%;
      }
      
      @media screen and (min-width: 2500px) {
        width: 14%;
      }
    `}

  ${({ extraLabelTop }) => extraLabelTop && css`
    margin-left: 36%;
    @media screen and (min-width: 1750px) {
      margin-left: 25%;
    }

    @media screen and (min-width: 2000px) {
      margin-left: 23%;
    }

    @media screen and (min-width: 2200px) {
      margin-left: 19%;
    }
    
    @media screen and (min-width: 2500px) {
      margin-left: 15%;
    }
  `}
    
  @media screen and (min-width: ${Breakpoints.md}) {
    display: block;
  }
`;

const Label = ({
  children,
  weight,
  setMinHeight,
  isRequired,
  hideLabelAsterisk,
  inline,
  inverse,
  fontSize,
  wordBreak,
  wordWrap,
  color,
  error,
  disabled,
  width,
  extraLabelTop,
  ...props
}) => (
  <Container
    color={color}
    width={width}
    wordBreak={wordBreak}
    wordWrap={wordWrap}
    setMinHeight={setMinHeight}
    isRequired={isRequired}
    inline={inline}
    inverse={inverse}
    extraLabelTop={extraLabelTop}
    {...props}
  >
    <LabelText disabled={disabled} weight={weight} fontSize={fontSize} color={color}>
      {children && isRequired && !hideLabelAsterisk ? '* ' : ''}
      {children}
    </LabelText>

    {error ? <LabelText style={{ display: 'block', marginTop: 15, whiteSpace: 'nowrap' }} weight={weight} fontSize={fontSize} color={color}>
      {error}
    </LabelText> : ''}
  </Container>
);


Label.propTypes = propTypes;
Label.defaultProps = defaultProps;

export default Label;
